import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Users, UserCheck, Database, Lightbulb } from "lucide-react";

interface Department {
  name: string;
  participants: number;
}

interface GSANetworkStatsProps {
  totalParticipants: number;
  activeContributors: number;
  knowledgeBases: number;
  totalSolutions: number;
  departments: Department[];
}

const GSANetworkStats = ({
  totalParticipants,
  activeContributors,
  knowledgeBases,
  totalSolutions,
  departments,
}: GSANetworkStatsProps) => {
  const totalDeptParticipants = departments.reduce((sum, dept) => sum + dept.participants, 0);
  const activePercentage = totalDeptParticipants > 0 
    ? Math.round((activeContributors / totalDeptParticipants) * 100) 
    : 0;

  return (
    <div className="space-y-6">
      {/* Top Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 text-center bg-white shadow-sm">
          <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
          <p className="text-2xl font-bold">{totalParticipants.toLocaleString()}</p>
          <p className="text-sm text-gray-600">Total Participants</p>
        </Card>
        
        <Card className="p-4 text-center bg-white shadow-sm">
          <UserCheck className="h-8 w-8 mx-auto mb-2 text-green-600" />
          <p className="text-2xl font-bold">{activeContributors}</p>
          <p className="text-sm text-gray-600">Active Contributors</p>
        </Card>
        
        <Card className="p-4 text-center bg-white shadow-sm">
          <Database className="h-8 w-8 mx-auto mb-2 text-purple-600" />
          <p className="text-2xl font-bold">{knowledgeBases}</p>
          <p className="text-sm text-gray-600">Knowledge Bases</p>
        </Card>
        
        <Link to="/gsa-solutions">
          <Card className="p-4 text-center bg-white shadow-sm cursor-pointer hover:shadow-md transition-shadow">
            <Lightbulb className="h-8 w-8 mx-auto mb-2 text-yellow-600" />
            <p className="text-2xl font-bold">{totalSolutions}</p>
            <p className="text-sm text-gray-600">Marketplace</p>
          </Card>
        </Link>
      </div>

      {/* Department Participation */}
      <Card className="p-6 bg-white shadow-sm">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span className="text-sm bg-gray-100 px-2 py-1 rounded">📊</span>
          GSA Participation by Department
        </h3>
        <div className="space-y-2">
          {departments.map((dept, index) => {
            const percentage = totalDeptParticipants > 0 
              ? Math.round((dept.participants / totalDeptParticipants) * 100) 
              : 0;
            
            return (
              <div key={index} className="space-y-1">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-medium">{dept.name}</span>
                  <span className="text-gray-600">
                    {dept.participants} participants ({percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Network Activity */}
      <Card className="p-6 bg-white shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Network Activity</h3>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-green-600 h-3 rounded-full transition-all flex items-center justify-end pr-2"
            style={{ width: `${activePercentage}%` }}
          >
            <span className="text-xs text-white font-bold">{activePercentage}% Active Contributors</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default GSANetworkStats;
